import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Button from "../components/Button";
import TextField from "../components/TextField";
import Table from "../components/Table";
import Tag from "../components/Tag";
import Pagination from "../components/Pagination";
import Dropdown from "../components/Dropdown";
import Modal from "../components/Modal";
import ChartSettings from "../components/ChartSettings";
import DownloadSettings from "../components/DownloadSettings";
import ShareSettings from "../components/ShareSettings";
import ShareEmailModal from "../components/ShareEmailModal";
import SpeciesSettings from "../components/SpeciesSettings.jsx";
import AuthorizationTypeSettings from "../components/AuthorizationTypeSettings";
import DateRangePicker from "../components/DateRangePicker";
import { loadFilterSection, saveFilterSection } from "../utils/filterStorage";
import { getCurrentSeasonDateRange } from "../utils/seasonDateRange";
import { formatNumber } from "../utils/formatNumber";
import "./styles.css";

// Chart.js imports
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Importar imágenes de animales
import imgCorzo from "../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png";
import imgJabali from "../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png";
import imgCabraMontes from "../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png";
import imgCiervo from "../assets/8c8152eb2021b80f4880dee496903dade728006a.png";
import imgPalomaTorcaz from "../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png";
import imgMuflon from "../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png";
import imgGamo from "../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png";
import imgRebeco from "../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png";
import imgTortola from "../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png";
import imgCodorniz from "../assets/img/especies/Codorniz.png";

// Importar iconos
import iconDownload from "../assets/icons/download.svg";
// import iconShare from '../assets/icons/share.svg'
import iconCog from "../assets/icons/settings.svg";
import iconTriangleUp from "../assets/icons/triangle-up.svg";
import iconTriangleDown from "../assets/icons/triangle-down.svg";

export default function Autorizaciones() {
    const globalFilters = loadFilterSection("global") || {};
    const autorizacionesFilters = loadFilterSection("autorizaciones") || {};
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissions, setPermissions] = useState(null);
    
    const [especies, setEspecies] = useState([]);
    const [modalidades, setModalidades] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [estados, setEstados] = useState([]);
    const [cotos, setCotos] = useState([]);
    const initialTotalAutorizaciones = {
        total: 0,
        difference: 0,
        isIncrease: false,
    };
    const initialAutorizacionesPorEspecie = [];
    const initialAutorizacionesPorFecha = [];
    const initialDistribucionSistema = {
        capturcyl: 0,
        papel: 0,
        porcentaje_capturcyl: 0,
        porcentaje_papel: 0,
    };
    const initialEstadoAutorizaciones = [];
    const initialTopCotos = [];
    const initialTotalCotos = 0;
    const initialTendenciasData = {
        capturcyl_difference: 0,
        capturcyl_is_increase: false,
    };
    const [initialAutorizacionesTable, setInitialAutorizacionesTable] = useState({
        data: [],
        current_page: 1,
        last_page: 4,
    });

    const [activeTab, setActiveTab] = useState("autorizaciones");
    const [currentPage, setCurrentPage] = useState(
        initialAutorizacionesTable.current_page,
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState(
        autorizacionesFilters.selectedPeriod || "mensual",
    );
    const [selectedProvincia, setSelectedProvincia] = useState(
        globalFilters.selectedProvincia || [],
    );
    const [selectedEstado, setSelectedEstado] = useState(
        autorizacionesFilters.selectedEstado || [],
    );
    const [selectedCoto, setSelectedCoto] = useState(
        globalFilters.selectedCoto || [],
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [isSpeciesModalOpen, setIsSpeciesModalOpen] = useState(false);
    const [isAuthorizationTypeModalOpen, setIsAuthorizationTypeModalOpen] =
        useState(false);
    //   const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false);
    const [chartType, setChartType] = useState("barras");
    const [fileType, setFileType] = useState("xls");
    const [shareType, setShareType] = useState("png");
    const [sortedColumn, setSortedColumn] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isLoadingTable, setIsLoadingTable] = useState(false);
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);

    // Estados para ordenamiento de la tabla
    const [sortBy, setSortBy] = useState("desde");
    const [sortOrder, setSortOrder] = useState("desc");
    // Data states
    const [totalAutorizaciones, setTotalAutorizaciones] = useState(
        initialTotalAutorizaciones,
    );
    const [autorizacionesPorEspecie, setAutorizacionesPorEspecie] = useState(
        initialAutorizacionesPorEspecie,
    );
    const [autorizacionesPorFecha, setAutorizacionesPorFecha] = useState(
        initialAutorizacionesPorFecha,
    );
    const [distribucionSistema, setDistribucionSistema] = useState(
        initialDistribucionSistema,
    );
    const [estadoAutorizaciones, setEstadoAutorizaciones] = useState(
        initialEstadoAutorizaciones,
    );
    const [topCotos, setTopCotos] = useState(initialTopCotos);
    const [totalCotos, setTotalCotos] = useState(initialTotalCotos);
    const [tendenciasData, setTendenciasData] = useState(initialTendenciasData);
    const [autorizacionesTable, setAutorizacionesTable] = useState(
        initialAutorizacionesTable,
    );

    // Cargar datos iniciales
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [especiesRes, modalidadesRes, provinciasRes, estadosRes, cotosRes, permissionsRes] = await Promise.all([
                api.get('/especies'),
                api.get('/modalidades'),
                api.get('/provincias'),
                api.get('/estados'),
                api.get('/cotos'),
                api.get('/auth/permissions')
            ]);

            // Transformar especies de {value, label} a {id, nombre}
            const especiesTransformadas = (especiesRes.data || []).map(especie => ({
                id: especie.value,
                nombre: especie.label
            }));

            setEspecies(especiesTransformadas);
            setModalidades(modalidadesRes.data || []);
            setProvincias((provinciasRes.data || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                }));
            setEstados(estadosRes.data || []);
            setCotos(cotosRes.data || []);
            setTotalCotos((cotosRes.data || []).length);
            setPermissions(permissionsRes.data?.permissions ?? permissionsRes.data ?? null);
        } catch (err) {
            if (err.response?.status !== 401) {
                Sentry.captureException(new Error('Error loading initial data: ' + err));
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    // Date range state
    const [dateRange, setDateRange] = useState(
        globalFilters.dateRange || getCurrentSeasonDateRange(),
    );

    // Lista de especies que se seleccionaban por defecto
    // const DEFAULT_SELECTED_SPECIES = [
    //     "Corzo",
    //     "Jabalí",
    //     "Cabra montés",
    //     "Ciervo",
    //     "Gamo",
    //     "Tórtola",
    // ];

    // Inicializar especies visibles basándose en las especies por defecto
    const [visibleSpecies, setVisibleSpecies] = useState(
        () => globalFilters.visibleSpecies || autorizacionesFilters.visibleSpecies || {},
    );

    const [visibleAuthorizationTypes, setVisibleAuthorizationTypes] = useState(
        autorizacionesFilters.visibleAuthorizationTypes || {
            jabaliRececho: true,
            jabaliEmergencia: true,
            caceriaColectiva: true,
        },
    );

    useEffect(() => {
        saveFilterSection("global", {
            dateRange,
            selectedProvincia,
            selectedCoto,
            visibleSpecies,
        });
    }, [dateRange, selectedProvincia, selectedCoto, visibleSpecies]);

    useEffect(() => {
        saveFilterSection("autorizaciones", {
            selectedPeriod,
            selectedEstado,
            visibleAuthorizationTypes,
        });
    }, [
        selectedPeriod,
        selectedEstado,
        visibleAuthorizationTypes,
    ]);

    const speciesSettingsRef = useRef(null);
    const authorizationTypeSettingsRef = useRef(null);
    const chartSettingsRef = useRef(null);
    const shareEmailModalRef = useRef(null);
    const pageContentRef = useRef(null);

    // Función para obtener el icono correspondiente a cada animal
    const getAnimalIcon = (nombreEspecie) => {
        const iconMap = {
            Corzo: imgCorzo,
            Jabalí: imgJabali,
            "Cabra montés": imgCabraMontes,
            Ciervo: imgCiervo,
            "Paloma torcaz": imgPalomaTorcaz,
            "Muflón": imgMuflon,
            Rebeco: imgRebeco,
            Gamo: imgGamo,
            "Tórtola europea": imgTortola,
            Codorniz: imgCodorniz,
        };
        return iconMap[nombreEspecie] || imgCorzo; // Default icon
    };

    // Función para descargar en Excel
    const downloadExcel = async () => {
        try {
            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();

            // Hoja 1: Resumen General
            const wsResumen = workbook.addWorksheet("Resumen");
            wsResumen.addRow(["Panel de Datos - Autorizaciones"]);
            wsResumen.addRow(["Fecha de generación:", new Date().toLocaleDateString("es-ES")]);
            wsResumen.addRow([
                "Rango de fechas:",
                `${formatDateForPicker(dateRange.startDate)} - ${formatDateForPicker(dateRange.endDate)}`,
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Métricas Principales"]);
            wsResumen.addRow(["Total Autorizaciones", totalAutorizaciones.total || 0]);
            wsResumen.addRow(["Diferencia mes anterior", totalAutorizaciones.difference || 0]);
            wsResumen.addRow([
                "Tendencia",
                totalAutorizaciones.isIncrease ? "Aumento" : "Disminución",
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Distribución Sistema"]);
            wsResumen.addRow(["Capturcyl", distribucionSistema.capturcyl || 0]);
            wsResumen.addRow(["En papel", distribucionSistema.papel || 0]);
            wsResumen.addRow([
                "Porcentaje Capturcyl (%)",
                distribucionSistema.porcentaje_capturcyl || 0,
            ]);
            wsResumen.addRow(["Porcentaje Papel (%)", distribucionSistema.porcentaje_papel || 0]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Total Cotos", totalCotos || 0]);

            // Hoja 2: Autorizaciones por Especie
            if (autorizacionesPorEspecie.length > 0) {
                const wsEspecie = workbook.addWorksheet("Por Especie");
                wsEspecie.addRow(["Especie", "Total Autorizaciones"]);
                autorizacionesPorEspecie
                    .filter((e) => visibleSpecies[e.especie_id])
                    .forEach((item) => {
                        wsEspecie.addRow([
                            item.nombre_especie,
                            item.total_autorizaciones || 0,
                        ]);
                    });
            }

            // Hoja 4: Estado de Autorizaciones
            if (estadoAutorizaciones.length > 0) {
                const wsEstado = workbook.addWorksheet("Estado");
                wsEstado.addRow(["Estado", "Cantidad", "Porcentaje (%)"]);
                estadoAutorizaciones.forEach((item) => {
                    wsEstado.addRow([
                        item.estado,
                        item.total,
                        item.porcentaje,
                    ]);
                });
            }

            // Hoja 6: Tabla de Autorizaciones - Obtener todos los registros
            try {
                const selectedSpeciesIds = Object.keys(visibleSpecies).filter(
                    (id) => visibleSpecies[id],
                );

                const params = new URLSearchParams({
                    search: searchTerm,
                    per_page: "999999", // Obtener todos los registros
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                    selected_species: JSON.stringify(selectedSpeciesIds),
                });

                // Añadir arrays de filtros
                if (
                    Array.isArray(selectedProvincia) &&
                    selectedProvincia.length > 0
                ) {
                    selectedProvincia.forEach((p) =>
                        params.append("provincia[]", p),
                    );
                }
                if (Array.isArray(selectedEstado) && selectedEstado.length > 0) {
                    selectedEstado.forEach((e) => params.append("estado[]", e));
                }
                if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                    selectedCoto.forEach((c) => params.append("coto_id[]", c));
                }

                const response = await api.get("/autorizaciones/data/table?" + params);
                const allData = response.data;

                if (allData.data && allData.data.length > 0) {
                    const wsTabla = workbook.addWorksheet("Tabla Autorizaciones");
                    wsTabla.addRow([
                        "Tipo",
                        "Nombre Coto",
                        "Matrícula",
                        "Provincia",
                        "Municipio",
                        "Titular / Gestor",
                        "Alias",
                        "Formato",
                        "Estado",
                        "Celebrada",
                        "Emisión",
                        "Fecha desde",
                        "Fecha hasta",
                        "Cazadores autorizados",
                        "Precintos",
                    ]);
                    allData.data.forEach((item) => {
                        wsTabla.addRow([
                            item.tipo,
                            item.nombre_coto || "",
                            item.matricula_coto || "",
                            item.nombre_provincia || "",
                            item.nombre_municipio || "",
                            item.nombre_titular,
                            item.nombre || "",
                            item.formato === 'app' ? 'CapturCyL' : item.formato === 'papel' ? 'En papel' : (item.formato || ""),
                            item.estado,
                            item.no_celebrada ? "No" : "Sí",
                            item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : "",
                            item.desde || "",
                            item.hasta || "",
                            item.total_cazadores ?? 0,
                            item.total_precintos ?? 0,
                        ]);
                    });
                }
            } catch (error) {
                if (error.response?.status !== 401) {
                    Sentry.captureException(new Error("Error loading all data for export: " + error));
                }
            }

            const fileName = `autorizaciones_${new Date().toISOString().split("T")[0]}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error exportando Excel: " + error.message));
            }
            alert("Error al generar el archivo Excel. Por favor, intente de nuevo.");
            console.error("Error en downloadExcel:", error);
        }
    };

    // Función para convertir imagen a base64
    const imageToBase64 = (img) => {
        return new Promise((resolve) => {
            if (!img.src || img.src.startsWith("data:")) {
                resolve(null);
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                ctx.drawImage(image, 0, 0);
                try {
                    const base64 = canvas.toDataURL("image/png");
                    resolve(base64);
                } catch (e) {
                    resolve(null);
                }
            };

            image.onerror = () => {
                resolve(null);
            };

            const separator = img.src.includes("?") ? "&" : "?";
            image.src = img.src + separator + "t=" + Date.now();
        });
    };

    // Función para descargar en PDF
    const downloadPDF = async () => {
        if (!pageContentRef.current) return;

        setIsDownloadModalOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
            // Imports dinámicos para mejor rendimiento
            const [html2canvasModule, jsPDFModule] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const html2canvas = html2canvasModule.default;
            const jsPDF = jsPDFModule.default;

            const loadingDiv = document.createElement("div");
            loadingDiv.id = "pdf-loading";
            loadingDiv.style.cssText =
                "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;";
            loadingDiv.innerHTML =
                '<div style="background: white; padding: 20px 40px; border-radius: 8px; font-size: 16px;">Generando PDF...</div>';
            document.body.appendChild(loadingDiv);

            const images = pageContentRef.current.querySelectorAll("img");
            const originalSrcs = new Map();

            for (const img of images) {
                if (img.src && !img.src.startsWith("data:")) {
                    originalSrcs.set(img, img.src);
                    const base64 = await imageToBase64(img);
                    if (base64) {
                        img.src = base64;
                    }
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 100));

            const canvas = await html2canvas(pageContentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: "#f5f5f5",
                imageTimeout: 0,
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation:
                    canvas.width > canvas.height ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;

            // * Quitamos el titulo por ahora *
            // pdf.setFontSize(16);
            // pdf.text("Panel de Datos - Autorizaciones", pdfWidth / 2, 10, {
            //     align: "center",
            // });

            const scaledHeight = imgHeight * ratio;
            const pageContentHeight = pdfHeight - 20;

            if (scaledHeight <= pageContentHeight) {
                pdf.addImage(
                    imgData,
                    "PNG",
                    imgX,
                    15,
                    imgWidth * ratio,
                    imgHeight * ratio,
                );
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let isFirstPage = true;

                while (remainingHeight > 0) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    const pageHeight = pageContentHeight / ratio;
                    const sliceHeight = Math.min(pageHeight, remainingHeight);

                    const tempCanvas = document.createElement("canvas");
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sliceHeight;
                    const tempCtx = tempCanvas.getContext("2d");
                    tempCtx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        imgWidth,
                        sliceHeight,
                        0,
                        0,
                        imgWidth,
                        sliceHeight,
                    );

                    const tempImgData = tempCanvas.toDataURL("image/png");
                    pdf.addImage(
                        tempImgData,
                        "PNG",
                        imgX,
                        isFirstPage ? 15 : 10,
                        imgWidth * ratio,
                        sliceHeight * ratio,
                    );

                    sourceY += sliceHeight;
                    remainingHeight -= sliceHeight;
                    isFirstPage = false;
                }
            }

            pdf.setFontSize(10);
            pdf.text(
                `Generado el ${new Date().toLocaleDateString("es-ES")} - Rango: ${formatDateForPicker(dateRange.startDate)} a ${formatDateForPicker(dateRange.endDate)}`,
                pdfWidth / 2,
                pdfHeight - 5,
                { align: "center" },
            );

            const fileName = `autorizaciones_${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);

            for (const [img, originalSrc] of originalSrcs) {
                img.src = originalSrc;
            }

            document.body.removeChild(loadingDiv);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error generating PDF: " + error));
            }
            const loadingDiv = document.getElementById("pdf-loading");
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert("Error al generar el PDF. Por favor, intente de nuevo.");
        }
    };

    // Función para manejar la descarga según el tipo seleccionado
    const handleDownload = () => {
        if (fileType === "xls") {
            downloadExcel();
            setIsDownloadModalOpen(false);
        } else if (fileType === "pdf") {
            downloadPDF();
        }
    };

    // Función para descargar solo los datos de la tabla en Excel
    const downloadTableExcel = async () => {
        if (isDownloadingExcel) return;
        setIsDownloadingExcel(true);
        try {
            const selectedSpeciesIds = Object.keys(visibleSpecies).filter(
                (id) => visibleSpecies[id],
            );

            // Obtener TODOS los datos del API sin paginación
            const params = new URLSearchParams({
                search: searchTerm,
                per_page: "999999", // Sin límite
                sort_by: sortBy,
                sort_order: sortOrder,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                selected_species: JSON.stringify(selectedSpeciesIds),
            });

            // Añadir arrays de filtros
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedEstado) && selectedEstado.length > 0) {
                selectedEstado.forEach((e) => params.append("estado[]", e));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/autorizaciones/data/table?" + params);
            const allData = response.data.data || [];

            if (allData.length === 0) {
                alert("No hay datos para descargar");
                return;
            }

            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();
            
            // Crear hoja de trabajo
            const worksheet = workbook.addWorksheet("Autorizaciones");
            
            // Columnas de exportación (incluye campos del detalle además de los de la tabla)
            const exportColumns = [
                { key: 'tipo', label: 'Tipo' },
                { key: 'nombre_coto', label: 'Nombre Coto' },
                { key: 'matricula_coto', label: 'Matrícula' },
                { key: 'nombre_provincia', label: 'Provincia' },
                { key: 'nombre_municipio', label: 'Municipio' },
                { key: 'nombre_titular', label: 'Titular / Gestor' },
                { key: 'nombre', label: 'Alias' },
                { key: 'formato', label: 'Formato' },
                { key: 'estado', label: 'Estado' },
                { key: 'no_celebrada', label: 'Celebrada' },
                { key: 'created_at', label: 'Emisión' },
                { key: 'desde', label: 'Fecha desde' },
                { key: 'hasta', label: 'Fecha hasta' },
                { key: 'total_cazadores', label: 'Cazadores autorizados' },
                { key: 'total_precintos', label: 'Precintos' },
            ];

            // Agregar encabezados
            const headers = exportColumns.map(col => col.label);
            const headerRow = worksheet.addRow(headers);

            // Estilo para encabezados
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2D6A4F' }
            };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

            // Agregar datos
            allData.forEach(row => {
                const rowData = exportColumns.map(col => {
                    const value = row[col.key];
                    if (col.key === 'no_celebrada') {
                        return value === 1 || value === true ? 'No' : 'Sí';
                    } else if (col.key === 'created_at') {
                        return value ? new Date(value).toLocaleDateString('es-ES') : '';
                    } else if (col.key === 'formato') {
                        return value === 'app' ? 'CapturCyL' : value === 'papel' ? 'En papel' : (value || '');
                    }
                    return value || '';
                });
                worksheet.addRow(rowData);
            });
            
            // Ajustar ancho de columnas
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index].length;
                column.eachCell({ includeEmpty: false }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 0;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 50);
            });
            
            // Generar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `autorizaciones_tabla_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
            if (error.response?.status !== 401) {
                Sentry.captureException(error);
            }
            alert("Hubo un error al descargar el archivo Excel");
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    const downloadPdfsZip = async () => {
        if (isDownloadingZip) return;
        setIsDownloadingZip(true);
        try {
            const selectedSpeciesIds = Object.keys(visibleSpecies).filter(
                (id) => visibleSpecies[id],
            );

            const params = new URLSearchParams({
                search: searchTerm,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                selected_species: JSON.stringify(selectedSpeciesIds),
            });

            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedEstado) && selectedEstado.length > 0) {
                selectedEstado.forEach((e) => params.append("estado[]", e));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/autorizaciones/export/pdfs-zip?" + params, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = `autorizaciones-pdfs-${new Date().toISOString().split("T")[0]}.zip`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar ZIP de PDFs:", error);
            if (error.response?.status !== 401) {
                Sentry.captureException(error);
            }
            const msg = error.response?.status === 404
                ? "No se encontraron archivos PDF para las autorizaciones filtradas"
                : "Hubo un error al descargar el ZIP de PDFs";
            alert(msg);
        } finally {
            setIsDownloadingZip(false);
        }
    };

    // Función para cargar datos dinámicamente
    const loadData = async () => {
        if (!dateRange.startDate || !dateRange.endDate) return;

        setIsLoadingData(true);
        try {
            const selectedSpeciesIds = Object.keys(visibleSpecies).filter(
                (id) => visibleSpecies[id],
            );

            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                period: selectedPeriod,
                selected_species: JSON.stringify(selectedSpeciesIds),
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            // Cargar datos de autorizaciones por fecha
            const fechaResponse = await api.get(
                `/autorizaciones/data/por-fecha?${params}`,
            );
            setAutorizacionesPorFecha(fechaResponse.data);

            // Cargar datos de autorizaciones por especie
            const especieResponse = await api.get(
                `/autorizaciones/data/por-especie?${params}`,
            );
            setAutorizacionesPorEspecie(especieResponse.data);

            // Cargar datos adicionales con los mismos filtros
            const [distribucionResponse, estadoResponse, topCotosResponse, totalResponse] = await Promise.all([
                api.get(`/autorizaciones/data/distribucion-sistema?${params}`),
                api.get(`/autorizaciones/data/estado?${params}`),
                api.get(`/autorizaciones/data/top-cotos?${params}`),
                api.get(`/autorizaciones/data/total?${params}`),
            ]);

            setDistribucionSistema(distribucionResponse.data);
            setEstadoAutorizaciones(estadoResponse.data);
            setTopCotos(topCotosResponse.data);
            setTotalAutorizaciones(totalResponse.data);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading data: " + error));
            }
        } finally {
            setIsLoadingData(false);
        }
    };

    // Cargar datos de la tabla con paginación, búsqueda y ordenamiento
    const loadTableData = async (
        page = 1,
        search = "",
        sortColumn = sortBy,
        sortDir = sortOrder,
        provincia = selectedProvincia,
        estado = selectedEstado,
        cotoId = selectedCoto,
    ) => {
        setIsLoadingTable(true);
        try {
            const selectedSpeciesIds = Object.keys(visibleSpecies).filter(
                (id) => visibleSpecies[id],
            );

            const params = new URLSearchParams({
                page: page.toString(),
                search: search,
                per_page: "10",
                sort_by: sortColumn,
                sort_order: sortDir,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                selected_species: JSON.stringify(selectedSpeciesIds),
            });

            // Añadir arrays de filtros
            if (Array.isArray(provincia) && provincia.length > 0) {
                provincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(estado) && estado.length > 0) {
                estado.forEach((e) => params.append("estado[]", e));
            }
            if (Array.isArray(cotoId) && cotoId.length > 0) {
                cotoId.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/autorizaciones/data/table?" + params);
            const data = response.data;
            setAutorizacionesTable(data);
            setCurrentPage(data.current_page);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading table data: " + error));
            }
        } finally {
            setIsLoadingTable(false);
        }
    };

    // Efecto para inicializar especies visibles cuando se cargan las especies
    useEffect(() => {
        if (!especies || especies.length === 0) return;

        if (Object.keys(visibleSpecies).length === 0) {
            const initial = {};
            especies.forEach((especie) => {
                initial[especie.id] = true;
            });
            setVisibleSpecies(initial);
            return;
        }

        const missing = especies.filter(
            (especie) => !Object.prototype.hasOwnProperty.call(visibleSpecies, especie.id),
        );
        if (missing.length > 0) {
            const next = { ...visibleSpecies };
            missing.forEach((especie) => { next[especie.id] = true; });
            setVisibleSpecies(next);
        }
    }, [especies, visibleSpecies]);

    // Efecto para cargar datos cuando cambian los filtros
    useEffect(() => {
        if (
            dateRange.startDate &&
            dateRange.endDate &&
            Object.keys(visibleSpecies).length > 0
        ) {
            loadData();
        }
    }, [
        dateRange.startDate,
        dateRange.endDate,
        selectedPeriod,
        visibleSpecies,
        selectedProvincia,
        selectedCoto,
    ]);

    // Función para convertir fecha ISO a formato DD/MM/YYYY
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    };

    const tabs = useMemo(() => {
        const allTabs = [
            { id: "general", label: "General" },
            { id: "usuarios", label: "Usuarios" },
            { id: "autorizaciones", label: "Autorizaciones" },
            { id: "cupos", label: "Cupos" },
            { id: "capturas", label: "Capturas" },
        ];
        // Temporalmente deshabilitado - mostrar siempre el tab de usuarios
        // Descomentar cuando el API devuelva correctamente canViewUsers
        
        if (!permissions?.canViewUsers) {
            return allTabs.filter((tab) => tab.id !== "usuarios");
        }
        
        return allTabs;
    }, [permissions?.canViewUsers]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        const routes = {
            general: "/general",
            usuarios: "/usuarios",
            cupos: "/cupos",
            capturas: "/capturas",
            autorizaciones: "/autorizaciones",
        };
        if (routes[tab]) navigate(routes[tab]);
    }, [navigate]);

    const periodOptions = useMemo(
        () => [
            { value: "mensual", label: "Mensual" },
            { value: "anual", label: "Anual" },
            { value: "semanal", label: "Semanal" },
        ],
        [],
    );

    // Configuraciones para gráficos de Chart.js
    const getSpeciesColors = useCallback(() => {
        const colors = [
            "#2D6A4F",
            "#40916C",
            "#52B788",
            "#74C69D",
            "#95D5B2",
            "#B7E4C7",
        ];
        return colors;
    }, []);

    // Configuración del gráfico de autorizaciones por fecha
    const autorizacionesPorFechaChartData = useMemo(
        () => ({
            labels: autorizacionesPorFecha.map((item) => item.fecha),
            datasets: [
                {
                    label: "Capturcyl",
                    data: autorizacionesPorFecha.map((item) =>
                        item.app ? item.app : 0,
                    ),
                    backgroundColor: "#2D6A4F",
                    borderColor: "#2D6A4F",
                    borderWidth: 1,
                },
                {
                    label: "En papel",
                    data: autorizacionesPorFecha.map((item) =>
                        item.papel ? item.papel : 0,
                    ),
                    backgroundColor: "#95D5B2",
                    borderColor: "#95D5B2",
                    borderWidth: 1,
                },
            ],
        }),
        [autorizacionesPorFecha],
    );

    // Configuración del gráfico de autorizaciones por especie
    const autorizacionesPorEspecieChartData = useMemo(
        () => {
            const especiesFiltradas = autorizacionesPorEspecie
                .filter((especie) => visibleSpecies[especie.especie_id])
                .sort((a, b) => a.nombre_especie.localeCompare(b.nombre_especie));
            
            return {
                labels: especiesFiltradas.map((item) => item.nombre_especie),
                datasets: [
                    {
                        label: "Autorizaciones",
                        data: especiesFiltradas.map((item) => item.total_autorizaciones),
                        backgroundColor: getSpeciesColors(),
                        borderColor: getSpeciesColors(),
                        borderWidth: 1,
                    },
                ],
            };
        },
        [autorizacionesPorEspecie, visibleSpecies, getSpeciesColors],
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                title: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    stacked: chartType === "barras",
                },
                x: {
                    stacked: chartType === "barras",
                },
            },
        }),
        [chartType],
    );

    // Opciones específicas para gráficos de distribución (barras/líneas)
    const distribucionChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                },
                x: {
                    grid: {
                        display: false,
                    },
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de distribución del sistema (para gráfico de dona)
    const distribucionSistemaPieData = {
        labels: ["Capturcyl", "En papel"],
        datasets: [
            {
                data: [
                    distribucionSistema.porcentaje_capturcyl,
                    distribucionSistema.porcentaje_papel,
                ],
                backgroundColor: ["#2D6A4F", "#95D5B2"],
                borderWidth: 0,
            },
        ],
    };

    const pieChartOptionsDistribucion = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            const label = context.label || '';
                            const cantidad = label === 'Capturcyl'
                                ? distribucionSistema.capturcyl
                                : distribucionSistema.papel;
                            const porcentaje = context.parsed;
                            return `${label}: ${formatNumber(cantidad)} (${porcentaje}%)`;
                        },
                    },
                },
            },
        }),
        [distribucionSistema],
    );

    // Configuración del gráfico de estado de autorizaciones
    const estadoAutorizacionesChartData = {
        labels: estadoAutorizaciones.map((item) => item.estado),
        datasets: [
            {
                data: estadoAutorizaciones.map((item) => item.porcentaje),
                backgroundColor: [
                    "#2D6A4F",
                    "#40916C",
                    "#52B788",
                    "#74C69D",
                    "#95D5B2",
                    "#B7E4C7"
                ],
                borderWidth: 0,
            },
        ],
    };

    const pieChartOptionsEstado = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            const index = context.dataIndex;
                            const estado = estadoAutorizaciones[index];
                            if (estado) {
                                return `${estado.estado}: ${formatNumber(estado.total)} (${estado.porcentaje}%)`;
                            }
                            return '';
                        },
                    },
                },
            },
        }),
        [estadoAutorizaciones],
    );

    // Configuración del gráfico de top cotos
    const topCotosChartData = {
        labels: topCotos.map(
            (item) => item.nombre_coto + " (" + item.matricula_coto + ")",
        ),
        datasets: [
            {
                label: "Autorizaciones",
                data: topCotos.map((item) => item.total_autorizaciones),
                backgroundColor: "#2D6A4F",
                borderColor: "#2D6A4F",
                borderWidth: 1,
            },
        ],
    };

    const horizontalBarOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Datos de la tabla (usar datos reales o fallback)
    const autorizacionesData = autorizacionesTable.data;

    const columns = [
        {
            key: "tipo",
            label: "Tipo",
            sortable: true,
            sortType: "alpha",
            minWidth: "350px",
            render: (value) => {
                if (!value) return "";
                return value
                    .split("_")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ");
            },
        },
        {
            key: "nombre_coto",
            label: "Nombre Coto",
            sortable: true,
        },
        {
            key: "matricula_coto",
            label: "Matrícula",
            sortable: true,
        },
        {
            key: "nombre_provincia",
            label: "Provincia",
            sortable: true,
        },
        {
            key: "nombre_municipio",
            label: "Municipio",
            sortable: true,
        },
        {
            key: "nombre_titular",
            label: "Titular / Gestor",
            sortable: true,
        },
        {
            key: "no_celebrada",
            label: "Celebrada",
            sortable: true,
            sortType: "alpha",
            render: (value) => {
                let variant = (value === true) ? "error" : "success";
                let realvalue = (value === true) ? "No" : "Si";
                return <Tag variant={variant}>{realvalue}</Tag>;
            },
        },
        {
            key: "created_at",
            label: "Fecha",
            sortable: true,
            render: (value) => {
                if (!value) return "";
                // Convertir de YYYY-MM-DDTHH:MM:SS.000000Z a DD-MM-YYYY
                const dateOnly = value.split("T")[0]; // Obtener solo YYYY-MM-DD
                const [year, month, day] = dateOnly.split("-");
                return `${day}-${month}-${year}`;
            },
        },
    ];

    // Handler para cambio de página
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    // Handler para búsqueda
    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset a la primera página al buscar
    }, []);

    // Handler para ordenamiento
    const handleSort = useCallback(
        (columnKey) => {
            setSortBy((prev) => {
                const newSortOrder =
                    prev === columnKey && sortOrder === "asc" ? "desc" : "asc";
                setSortOrder(newSortOrder);
                loadTableData(
                    1,
                    searchTerm,
                    columnKey,
                    newSortOrder,
                    selectedProvincia,
                    selectedEstado,
                    selectedCoto,
                );
                return columnKey;
            });
        },
        [
            sortOrder,
            searchTerm,
            selectedProvincia,
            selectedEstado,
            selectedCoto,
        ],
    );

    // Handler para click en fila
    const handleRowClick = (row) => {
        navigate(`/autorizaciones/${row.id}`);
    };

    // Efecto consolidado para cargar datos de la tabla
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            loadTableData(
                currentPage,
                searchTerm,
                sortBy,
                sortOrder,
                selectedProvincia,
                selectedEstado,
                selectedCoto,
            );
        }
    }, [
        currentPage,
        dateRange.startDate,
        dateRange.endDate,
        searchTerm,
        sortBy,
        sortOrder,
        selectedProvincia,
        selectedEstado,
        selectedCoto,
        visibleSpecies,
    ]);

    return (
        <>
            {loading && <LoadingScreen />}
            {error && <div className="error-message">Error: {error}</div>}
            {!loading && !error && (
        <div className="page-autorizaciones" ref={pageContentRef}>
            <h1 className="page-title">Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León</h1>

            <div className="page-tabs">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>

            <div className="page-content">
                <div className="page-filters">
                    <div style={{display: 'flex', gap: '35px', flexWrap: 'wrap'}}>
                        <div className="filter-group">
                            <label className="filter-label">
                                Seleccione un rango de fechas
                            </label>
                            <DateRangePicker
                                startDate={formatDateForPicker(dateRange.startDate)}
                                endDate={formatDateForPicker(dateRange.endDate)}
                                onChange={setDateRange}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Provincia
                            </label>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Coto
                            </label>
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Especies
                            </label>
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() =>
                                    setIsSpeciesModalOpen(true)
                                }
                            >
                                {/* <img src={iconCog} alt="" /> */}
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="filter-actions">
                        <Button
                            variant="accent"
                            onClick={() => setIsDownloadModalOpen(true)}
                        >
                            <img
                                src={iconDownload}
                                alt=""
                                className="button-icon button-icon-white"
                            />
                            <span>Descargar</span>
                        </Button>
                        {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
              <img src={iconShare} alt="" />
            </Button> */}
                    </div>
                </div>

                {/* KPIs principales */}
                <div className="panel-section-title">Resumen</div>
                <div className="panel-kpi-row">
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Total autorizaciones</span>
                        <span className="kpi-value">
                            {formatNumber(totalAutorizaciones.total)}
                        </span>
                        <span className="kpi-foot">Periodo seleccionado</span>
                    </div>
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Capturcyl</span>
                        <span className="kpi-value">
                            {formatNumber(distribucionSistema.capturcyl)}
                        </span>
                        <span className="kpi-foot">
                            {distribucionSistema.porcentaje_capturcyl
                                ? distribucionSistema.porcentaje_capturcyl + "% del total"
                                : ""}
                        </span>
                    </div>
                    <div className="panel-kpi-card">
                        <span className="kpi-label">En papel</span>
                        <span className="kpi-value">
                            {formatNumber(distribucionSistema.papel)}
                        </span>
                        <span className="kpi-foot">
                            {distribucionSistema.porcentaje_papel
                                ? distribucionSistema.porcentaje_papel + "% del total"
                                : ""}
                        </span>
                    </div>
                </div>

                {/* Especies registradas + Autorizaciones por especie */}
                <div className="panel-section-title">Especies registradas</div>
                <Card>
                    <div className="animals-grid">
                        {autorizacionesPorEspecie
                            ?.filter((especie) => visibleSpecies[especie.especie_id])
                            .sort((a, b) =>
                                a.nombre_especie.localeCompare(b.nombre_especie),
                            )
                            .map((especie) => (
                                <div
                                    key={especie.especie_id}
                                    className="animal-item"
                                >
                                    <img
                                        src={getAnimalIcon(especie.nombre_especie)}
                                        alt={especie.nombre_especie}
                                        className="animal-icon"
                                    />
                                    <div className="animal-info">
                                        <p className="animal-name">
                                            {especie.nombre_especie}
                                        </p>
                                        <p className="animal-value">
                                            {formatNumber(especie.total_autorizaciones)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="card-section">
                        <div className="card-section-header">
                            <p className="card-section-title">
                                Autorizaciones por especie
                            </p>
                            <div className="card-actions">
                                <Button
                                    variant="primary"
                                    size="s"
                                    onClick={() =>
                                        setIsAuthorizationTypeModalOpen(true)
                                    }
                                >
                                    <img src={iconCog} alt="" />
                                </Button>
                            </div>
                        </div>
                        <div
                            className="chart-container-large card-section-chart"
                            style={{ height: "240px" }}
                        >
                            {isLoadingData ? (
                                <div style={{ color: "var(--dk-green-600)" }}>
                                    Cargando datos...
                                </div>
                            ) : autorizacionesPorEspecieChartData.labels.length > 0 ? (
                                chartType === "lineal" ? (
                                    <Line
                                        data={autorizacionesPorEspecieChartData}
                                        options={chartOptions}
                                    />
                                ) : (
                                    <Bar
                                        data={autorizacionesPorEspecieChartData}
                                        options={chartOptions}
                                    />
                                )
                            ) : (
                                <div style={{ color: "var(--neutral-500)" }}>
                                    No hay datos disponibles
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Análisis - gráficos */}
                <div className="panel-section-title">Análisis</div>
                <div className="panel-charts-grid">
                    {/* Distribución del sistema */}
                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Distribución del sistema
                                </p>
                            </div>
                            <div
                                className="card-pie-chart"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "240px",
                                }}
                            >
                                <div style={{ width: "220px", height: "220px" }}>
                                    <Doughnut
                                        data={distribucionSistemaPieData}
                                        options={pieChartOptionsDistribucion}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="page-table-section">
                    <div className="table-section-header">
                        <h3 className="table-section-title">Autorizaciones</h3>
                    </div>
                    <div className="table-filters">
                        <TextField
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            withIcon={true}
                            className="table-search"
                        />
                        <Dropdown
                            options={provincias}
                            value={selectedProvincia}
                            onChange={(value) => {
                                setSelectedProvincia(value);
                                setCurrentPage(1);
                            }}
                            placeholder="Todas las provincias"
                            searchable={true}
                            multiple={true}
                        />
                        <Dropdown
                            options={cotos}
                            value={selectedCoto}
                            onChange={(value) => {
                                setSelectedCoto(value);
                                setCurrentPage(1);
                            }}
                            placeholder="Todos los cotos"
                            searchable={true}
                            multiple={true}
                        />
                        <Button
                            variant="primary"
                            size="s"
                            style={{ position: 'relative' }}
                            onClick={() => setIsSpeciesModalOpen(true)}
                        >
                            Selecciona las especies
                            {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                    {Object.values(visibleSpecies).filter(Boolean).length}
                                </span>
                            )}
                        </Button>
                        <Button
                            variant="accent"
                            size="s"
                            onClick={downloadTableExcel}
                            title="Descargar tabla en Excel"
                            disabled={isDownloadingExcel}
                        >
                            {isDownloadingExcel ? "..." : (
                                <img
                                    src={iconDownload}
                                    alt=""
                                    className="button-icon button-icon-white"
                                />
                            )}
                        </Button>
                        {permissions?.canViewVisor && (
                            <Button
                                variant="accent"
                                size="s"
                                onClick={downloadPdfsZip}
                                title="Descargar ZIP de PDFs"
                                disabled={isDownloadingZip}
                            >
                                {isDownloadingZip ? "Descargando..." : "Descargar ZIP"}
                            </Button>
                        )}
                    </div>
                    {isLoadingTable ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "40px",
                                color: "var(--dk-green-600)",
                            }}
                        >
                            Cargando datos...
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            data={autorizacionesTable.data || []}
                            onSort={handleSort}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            isLoading={isLoadingTable}
                            onRowClick={handleRowClick}
                        />
                    )}
                    <div className="table-pagination">
                        <Pagination
                            currentPage={
                                autorizacionesTable.current_page || currentPage
                            }
                            totalPages={autorizacionesTable.last_page || 4}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApply={() => {
                    if (chartSettingsRef.current) {
                        chartSettingsRef.current.apply();
                    }
                    setIsModalOpen(false);
                }}
            >
                <ChartSettings
                    ref={chartSettingsRef}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                />
            </Modal>

            <Modal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onApply={handleDownload}
                title="Descargar"
            >
                <DownloadSettings
                    fileType={fileType}
                    onFileTypeChange={setFileType}
                />
            </Modal>

            <Modal
                isOpen={isSpeciesModalOpen}
                onClose={() => setIsSpeciesModalOpen(false)}
                onApply={() => {
                    if (speciesSettingsRef.current) {
                        speciesSettingsRef.current.apply();
                        setIsSpeciesModalOpen(false);
                    }
                }}
            >
                <SpeciesSettings
                    ref={speciesSettingsRef}
                    especies={especies}
                    visibleSpecies={visibleSpecies}
                    onSpeciesChange={setVisibleSpecies}
                />
            </Modal>

            <Modal
                isOpen={isAuthorizationTypeModalOpen}
                onClose={() => setIsAuthorizationTypeModalOpen(false)}
                onApply={() => {
                    if (authorizationTypeSettingsRef.current) {
                        authorizationTypeSettingsRef.current.apply();
                    }
                    setIsAuthorizationTypeModalOpen(false);
                }}
            >
                <AuthorizationTypeSettings
                    ref={authorizationTypeSettingsRef}
                    authorizationTypes={visibleAuthorizationTypes}
                    onAuthorizationTypesChange={setVisibleAuthorizationTypes}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                    modalidades={modalidades}
                />
            </Modal>

            {/* <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal> */}

            <Modal
                isOpen={isShareEmailModalOpen}
                onClose={() => setIsShareEmailModalOpen(false)}
                onApply={() => {
                    if (shareEmailModalRef.current) {
                        shareEmailModalRef.current.send();
                    }
                }}
                title="Compartir"
                applyLabel="Enviar"
                cancelLabel="Cancelar"
            >
                <ShareEmailModal
                    ref={shareEmailModalRef}
                    onSend={(email) => {
                        setIsShareEmailModalOpen(false);
                        // TODO: Implementar envío de archivo por email según shareType
                    }}
                    onCancel={() => setIsShareEmailModalOpen(false)}
                />
            </Modal>
        </div>
            )}
        </>
    );
}


